import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, EyeOff, Check, ExternalLink } from "lucide-react";

function EventRow({ event, actions }) {
  return (
    <div
      data-testid={`mod-event-${event.id}`}
      className="card-warm rounded-sm p-5 flex items-start justify-between gap-6"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline" style={{ color: "var(--terracotta)" }}>
            {event.city} · {event.date}
          </span>
          {event.report_count > 0 && (
            <span
              className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}
              data-testid={`mod-report-count-${event.id}`}
            >
              {event.report_count} report{event.report_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="font-serif text-2xl mt-1 truncate">{event.title}</div>
        <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
          By {event.organizer_name}
          {event.organizer_verified ? " · verified" : " · unverified"}
        </div>
        <p className="mt-3 text-sm line-clamp-3" style={{ color: "var(--ink-soft)" }}>{event.description}</p>
        <div className="mt-3 flex items-center gap-3">
          <Link
            to={`/events/${event.id}`}
            data-testid={`mod-view-${event.id}`}
            className="text-xs uppercase tracking-widest hover:opacity-70"
            style={{ color: "var(--ink-soft)" }}
          >
            View event →
          </Link>
          {event.venue_url && (
            <a
              href={event.venue_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest hover:opacity-70"
              style={{ color: "var(--ink-soft)" }}
            >
              <ExternalLink className="h-3 w-3" /> Venue link
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-shrink-0">{actions}</div>
    </div>
  );
}

export default function AdminModeration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState({ pending: [], reported: [], hidden: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/moderation/queue");
      setQueue(data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin only");
        navigate("/");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load queue");
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/events/${id}/approve`);
      toast.success("Event approved & live");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const hide = async (id) => {
    const reason = window.prompt("Reason for hiding this event?", "hidden by admin");
    if (reason === null) return;
    try {
      await api.post(`/admin/events/${id}/hide`, { reason });
      toast.success("Event hidden");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const verifyOrganizer = async (organizerId) => {
    try {
      const { data } = await api.post(`/admin/organizers/${organizerId}/verify`, { verified: true });
      toast.success(`Organizer verified. Their pending events auto-approved.`);
      load();
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (!user) return <div className="p-20 text-center font-serif text-2xl">Log in as admin.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Trust & safety</div>
      <h1 className="font-serif text-5xl mt-2">Moderation queue.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
        Approve new organizers&rsquo; drafts, review community reports, and toggle events on or off.
      </p>

      {loading ? (
        <div className="text-center py-20 font-serif text-2xl">Loading…</div>
      ) : (
        <div className="mt-10 space-y-14">
          <Section
            title="Pending review"
            subtitle="Events from unverified organizers awaiting your approval."
            testid="mod-pending"
            empty="Inbox zero — no drafts to review."
            items={queue.pending.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                actions={
                  <>
                    <Button
                      data-testid={`mod-approve-${e.id}`}
                      className="btn-primary rounded-full"
                      onClick={() => approve(e.id)}
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve event
                    </Button>
                    <Button
                      data-testid={`mod-verify-org-${e.id}`}
                      variant="outline"
                      className="rounded-full"
                      onClick={() => verifyOrganizer(e.organizer_id)}
                    >
                      <ShieldCheck className="h-4 w-4 mr-1" /> Verify organizer
                    </Button>
                    <Button
                      data-testid={`mod-hide-${e.id}`}
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => hide(e.id)}
                    >
                      <EyeOff className="h-4 w-4 mr-1" /> Hide
                    </Button>
                  </>
                }
              />
            ))}
          />

          <Section
            title="Reported by community"
            subtitle="Events flagged by vendors. 3+ reports auto-hides the event."
            testid="mod-reported"
            empty="No reports right now."
            items={queue.reported.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                actions={
                  <>
                    <Button
                      data-testid={`mod-hide-${e.id}`}
                      className="btn-primary rounded-full"
                      onClick={() => hide(e.id)}
                    >
                      <EyeOff className="h-4 w-4 mr-1" /> Hide event
                    </Button>
                    <Button
                      data-testid={`mod-approve-${e.id}`}
                      variant="outline"
                      className="rounded-full"
                      onClick={() => approve(e.id)}
                    >
                      Keep live
                    </Button>
                  </>
                }
              />
            ))}
          />

          <Section
            title="Hidden"
            subtitle="Events removed from public listings. Restore any time."
            testid="mod-hidden"
            empty="Nothing hidden. Nice."
            items={queue.hidden.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                actions={
                  <Button
                    data-testid={`mod-approve-${e.id}`}
                    className="btn-primary rounded-full"
                    onClick={() => approve(e.id)}
                  >
                    <Check className="h-4 w-4 mr-1" /> Restore
                  </Button>
                }
              />
            ))}
          />
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, items, empty, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline justify-between border-b pb-3" style={{ borderColor: "#ece4d6" }}>
        <div>
          <h2 className="font-serif text-3xl">{title}</h2>
          <div className="mt-1 text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            {items.length} {items.length === 1 ? "event" : "events"}
          </div>
        </div>
        <div className="text-xs max-w-xs text-right" style={{ color: "var(--ink-soft)" }}>{subtitle}</div>
      </div>
      <div className="mt-6 space-y-4">
        {items.length === 0 ? (
          <div className="card-warm p-10 rounded-sm text-center font-serif text-xl" style={{ color: "var(--ink-soft)" }}>
            {empty}
          </div>
        ) : items}
      </div>
    </section>
  );
}
